import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { metricsService } from '../services/metricsService';
import { isAlarming, quotaLeftMinutes, quotaLevel } from '../lib/quota';

/**
 * Предупреждение о заканчивающемся пакете минут над рабочими вкладками.
 *
 * Пока запаса хватает — молчит. До фазы 3 человек узнавал об исчерпании только
 * из отказа при загрузке файла: строка «осталось N мин» в сайдбаре набрана
 * десятым кеглем, а полоса в личном кабинете видна, только если туда зайти.
 *
 * 🔴 Участнику организации это ЕДИНСТВЕННЫЙ сигнал: письма об остатке уходят
 * только владельцу — рядовому сотруднику они бессильны, он не может ни купить,
 * ни перераспределить. Поэтому гейт по роли здесь обязан пускать участника.
 */
export default function QuotaBanner({ email, role, onOpenPricing }) {
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    let alive = true;
    // Квоту спрашиваем ВСЕГДА, если известна почта. Раньше запрос стоял за
    // проверкой роли — но факт членства в организации приходит именно в этом
    // ответе (org_id), и при роли user до него дело не доходило бы никогда.
    if (!email) return undefined;
    metricsService.getQuota(email)
      .then((q) => { if (alive) setQuota(q); })
      .catch(() => {});     // best-effort: недоступные метрики не должны мешать работать
    return () => { alive = false; };
  }, [email]);

  // Показываем клиенту и участнику организации. Сотруднику Лаборатории вне
  // организации — нет: у роли user лимит существует, но НЕ применяется,
  // превышение даёт log_warn. 16.08.2026 баннер показали всем, и 182 человека
  // получили тревогу о том, что их ничем не ограничивает.
  const inOrg = !!(quota && quota.org_id);
  if (role !== 'client' && !inOrg) return null;

  const level = quotaLevel(quota);
  if (!isAlarming(level)) return null;

  const left = quotaLeftMinutes(quota);
  const empty = level === 'empty';
  const critical = level === 'critical';
  const orgName = quota && quota.org_name;

  return (
    <div
      data-testid="quota-banner"
      className={`mb-4 flex items-start gap-2 rounded border p-3 text-sm ${
        empty || critical ? 'bg-paper-accent/[0.08] text-paper-accent'
                          : 'bg-paper-panel text-paper-warning'
      }`}
    >
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
      <span className="flex-1">
        {empty
          ? `Пакет минут ${inOrg ? `организации «${orgName}»` : 'по вашему тарифу'} исчерпан.`
          : `Пакет минут ${inOrg ? `организации «${orgName}»` : ''} заканчивается: осталось ${left}.`}
        {inOrg && !quota.org_is_owner && (
          // Рядовому участнику кнопка «к тарифам» не нужна: покупает плательщик.
          // Ему нужно знать, к кому идти, — иначе он пойдёт в поддержку.
          <span className="ml-1">Минуты общие — сообщите плательщику организации.</span>
        )}
        {(role === 'client' || (inOrg && quota.org_is_owner)) && (
          <button
            type="button"
            className="ml-2 font-semibold underline hover:no-underline"
            onClick={onOpenPricing}
          >
            Перейти к тарифам
          </button>
        )}
      </span>
    </div>
  );
}
